import { ApprovalTargetLockState } from '@watershed/constants/dataGovApprovals';
import { CanonicalDatasetKind } from '@watershed/constants/datasets';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';

/**
 * This context data is strictly facilities metadata used within generic datasource spaces
 * (ex. within the task page which applies to both facilities/non-facilities datasets).
 *
 * Facilities-custom spaces should have their own context providers which will never be used
 * in dataset-agnostic components and thus can make more assertions about the data.
 */
type DatasourcePageContextFacilitiesData = {
  previewFacilitiesCount: number | null;
};

/**
 * This is data that affects the entire Datasource task page:
 * - thin, top-level metadata used all over
 *   (ex. id's and state for measurement project, task, datasource, etc.)
 * - refresh state information (like refreshing uploads, isLoading, etc.)
 * - event target for coordinating updates across components
 */
export type DatasourcePageContextData = {
  canonicalDatasetId: string | undefined;
  canonicalDatasetKind: CanonicalDatasetKind | undefined;
  datasetId: string;
  datasourceId: string;
  facilitiesData: DatasourcePageContextFacilitiesData | undefined;
  userUploadTaskLockState: ApprovalTargetLockState;
  userUploadTaskId: string;
  eventTarget: EventTarget;
};

export const DatasourcePageContext = createContext<DatasourcePageContextData>({
  canonicalDatasetId: undefined,
  canonicalDatasetKind: undefined,
  datasetId: '',
  datasourceId: '',
  facilitiesData: undefined,
  userUploadTaskLockState: ApprovalTargetLockState.Unlocked,
  userUploadTaskId: '',
  eventTarget: new EventTarget(),
});

export const useDatasourcePageContext = () => {
  return useContext(DatasourcePageContext);
};

export const USER_UPLOAD_RELATED_DATA_CHANGED_EVENT =
  'userUploadRelatedDataChanged';
export const useHandlerForUploadsChangedEvent = (handler: () => void) => {
  const { eventTarget } = useDatasourcePageContext();
  const isHandlingRef = useRef(false);

  const protectedHandler = useCallback(() => {
    // Prevent re-entrant calls that would cause infinite loops
    if (isHandlingRef.current) {
      console.warn(
        '[useHandlerForUploadsChangedEvent] Handler already running, skipping to prevent infinite loop'
      );
      return;
    }

    isHandlingRef.current = true;
    try {
      handler();
    } finally {
      isHandlingRef.current = false;
    }
  }, [handler]);

  useEffect(() => {
    eventTarget.addEventListener(
      USER_UPLOAD_RELATED_DATA_CHANGED_EVENT,
      protectedHandler
    );
    return () => {
      eventTarget.removeEventListener(
        USER_UPLOAD_RELATED_DATA_CHANGED_EVENT,
        protectedHandler
      );
    };
  }, [eventTarget, protectedHandler]);
};
